import {isMountedInShell} from '../../shellRemote';
import type {RedirectLocation} from '../types';

/**
 * These published-work paths should be kept for backwards compatibility. This was a quick hack job
 * (before gallery) to satisfy a few customers who wanted to publicize reports while using data from
 * a private project. The way to create such a report is via a very old script that (hopefully) isn't
 * being used anymore. This should be phased out in the future, but there are a few reports that still
 * utilize this path (eg. openai).
 */
export const PUBLISHED_WORK_PATH_SEGMENT = 'published-work';
export const REPORT_PAGE_VIEW_PUBLISHED_WORK_PATH = `/:entityName/${PUBLISHED_WORK_PATH_SEGMENT}/:reportNameAndID`;

/**
 * These gallery paths are "old fully connected" paths that should be kept for backwards compatibility.
 * Ideally, this should be phased out. Gallery and FC are terms that are used all over the codebase,
 * so the terminology also needs to be updated.
 */

export const LEGACY_GALLERY_PATH_SEGMENT = 'fully-connected';
export const FORGE_GALLERY_PATH_SEGMENT = 'forge-reports';
export const gallerySegment = (isForge: boolean) =>
  isForge ? FORGE_GALLERY_PATH_SEGMENT : LEGACY_GALLERY_PATH_SEGMENT;
export const GALLERY_PATH_SEGMENT = gallerySegment(isMountedInShell());

export const GALLERY_PATH = [
  `/${GALLERY_PATH_SEGMENT}/projects/:repoName`,
  `/${GALLERY_PATH_SEGMENT}/topics/:topic`,
  `/${GALLERY_PATH_SEGMENT}/:page?/:tag?/:reportID?`,
];

/**
 * The segment the gallery used before `GALLERY_PATH_SEGMENT`. Retired: the host
 * 301s it, and these keep in-app navigation working for anything still linking
 * to it.
 */
export const FULLY_CONNECTED_LEGACY_PATH = `/${LEGACY_GALLERY_PATH_SEGMENT}`;

/**
 * Never self-map a retired section: a same-path redirect can loop forever.
 */
export const FC_REDIRECTS: Record<string, string> = {
  'gradient-dissent': 'podcast',
  NLP: 'nlp',
  'keras-or-tensorflow': 'keras',
  scikit: 'sklearn',
  visualization: 'computer-vision',
  webinar: 'events',
  AMA: 'events',
  'paper-reading-group': 'events',
  blog: '',
  'two-minute-papers': '',
  'reproducibility-challenge': '',
  'paper-overview': '',
  tutorials: '',
  question: '',
  resources: '',
  showcase: '',
  salon: '',
  fastbook: '',
};

// Mirrors FC_PAGES in util/gallery/shared.ts.
export const FC_PAGE_SEGMENTS = new Set([
  'blog',
  'ml-news',
  'podcast',
  'events',
  'projects',
  'topics',
]);

/**
 * Maps the segments that followed the old gallery segment onto the current
 * one. Tag pages used to live at `<segment>/blog/<tag>`, and the topic nav
 * linked them as `<segment>/blog/tag/<tag>`; both collapse to
 * `<segment>/<tag>`.
 *
 * Shared with the host's `redirectToGalleryPath` so a path forwarded by the
 * server and the same path forwarded in-app resolve identically.
 */
export function galleryPathFromLegacySegments(segments: string[]): string {
  let rest = segments;
  if (rest[0] === 'blog') {
    rest = rest.slice(1);
    if (rest[0] === 'tag') {
      rest = rest.slice(1);
    }
  }
  if (
    rest.length > 0 &&
    Object.prototype.hasOwnProperty.call(FC_REDIRECTS, rest[0])
  ) {
    const replacement = FC_REDIRECTS[rest[0]];
    rest = replacement === '' ? rest.slice(1) : [replacement, ...rest.slice(1)];
  }
  return '/' + [FORGE_GALLERY_PATH_SEGMENT, ...rest].join('/');
}

export function redirectOffFullyConnected({
  pathname,
  search = '',
  hash = '',
}: RedirectLocation): string {
  const segments = pathname
    .slice(FULLY_CONNECTED_LEGACY_PATH.length)
    .split('/')
    .filter(s => s !== '');
  return `${galleryPathFromLegacySegments(segments)}${search}${hash}`;
}

export function redirectOffForgeReports({
  pathname,
  search = '',
  hash = '',
}: RedirectLocation): string {
  const legacyPath = pathname.replace(
    new RegExp(`^/${FORGE_GALLERY_PATH_SEGMENT}(?=/|$)`),
    FULLY_CONNECTED_LEGACY_PATH
  );
  const legacySegments = legacyPath
    .slice(FULLY_CONNECTED_LEGACY_PATH.length)
    .split('/')
    .filter(s => s !== '');
  if (legacySegments.length > 0 && !FC_PAGE_SEGMENTS.has(legacySegments[0])) {
    legacySegments.unshift('blog');
  }
  const targetPath =
    legacySegments.length === 0
      ? FULLY_CONNECTED_LEGACY_PATH
      : `${FULLY_CONNECTED_LEGACY_PATH}/${legacySegments.join('/')}`;
  return `${targetPath}${search}${hash}`;
}

export const INACTIVE_GALLERY_SEGMENT_PATH = isMountedInShell()
  ? FULLY_CONNECTED_LEGACY_PATH
  : `/${FORGE_GALLERY_PATH_SEGMENT}`;
export const redirectOffInactiveGallerySegment = isMountedInShell()
  ? redirectOffFullyConnected
  : redirectOffForgeReports;

/**
 * @deprecated - this is no longer a valid path, but keeping it around for historical context
 * This path was used when clicking on "Write post" in fully connected
 */
export const GALLERY_CREATE_POST = `/${GALLERY_PATH_SEGMENT}/create-post`;
/**
 * @deprecated - this is no longer a valid path, but keeping it around for historical context
 * This path was used when clicking on "Write post" in fully connected
 */
export const GALLERY_POST_VIEW_PATH = '/:entityName/posts/:reportNameAndID';
/**
 * @deprecated - this is no longer a valid path, but keeping it around for historical context
 * This path was used when clicking on "Write post" in fully connected
 */
export const GALLERY_POST_EDIT_PATH = `${GALLERY_POST_VIEW_PATH}/edit`; // redirects to view mode

export const REPORT_PAGE_VIEW_PATH =
  '/:entityName/:projectName/reports/:reportNameAndID';
export const REPORT_PAGE_EDIT_PATH = `${REPORT_PAGE_VIEW_PATH}/edit`;

// Deprecated report paths that redirect to /reportlist now
export const OLD_REPORT_PAGE_VIEW_PATH = '/:entityName/:projectName/published';
export const OLD_REPORT_PAGE_EDIT_PATH = '/:entityName/:projectName/reports';

// Old featured report paths that go through the fully connected paths now because
// there is no way to trigger create-discussion anymore
export const OLD_GALLERY_DISCUSSION_VIEW_PATH =
  '/:entityName/discussions/:reportNameAndID';
export const OLD_GALLERY_DISCUSSION_EDIT_PATH = `${OLD_GALLERY_DISCUSSION_VIEW_PATH}/edit`; // redirects to view mode
